import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { openDownload } from "@/lib/downloads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  PackageSearch, Plus, ScanLine, Truck, Warehouse, User as UserIcon,
  FileDown, Loader2, Search, AlertCircle, Printer, CheckSquare, Square,
} from "lucide-react";
import { toast } from "sonner";

const BRAND_OPTIONS = ["Autel", "Bosch", "Hunter", "Snap-on", "Launch", "Hella Gutmann", "Texa", "John Bean", "Car-O-Liner", "Other"];
const CATEGORY_OPTIONS = ["scanner", "target", "camera", "cable", "wheel-alignment", "calibration-rig", "accessory", "battery", "tool"];

export default function Equipment() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterBrand, setFilterBrand] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterLocation !== "all") params.location = filterLocation;
      if (filterBrand !== "all") params.brand = filterBrand;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/equipment", { params });
      setItems(r.data);
    } catch {
      toast.error("Failed to load equipment");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filterLocation, filterBrand]);

  const stats = useMemo(() => {
    const total = items.length;
    const inStorage = items.filter((i) => i.location === "storage").length;
    const inVan = items.filter((i) => i.location === "van").length;
    const withTech = items.filter((i) => i.location === "technician").length;
    return { total, inStorage, inVan, withTech };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <PackageSearch className="w-7 h-7 text-blue-400" /> Equipment Inventory
          </h1>
          <p className="text-slate-400 text-sm mt-1">Track Autel scanners, targets, calibration rigs across Van & Storage with QR codes and signature forms.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="equipment-scan-btn"
            variant="outline"
            className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
            onClick={() => navigate("/equipment/scan")}
          >
            <ScanLine className="w-4 h-4 mr-2" /> Scan QR
          </Button>
          <Button
            data-testid="equipment-labels-btn"
            variant="outline"
            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
            onClick={() => setLabelOpen(true)}
            disabled={items.length === 0}
          >
            <Printer className="w-4 h-4 mr-2" /> Print Labels
          </Button>
          <button
            type="button"
            onClick={() => openDownload("/api/equipment/report.pdf", "equipment_report_pdf", "all")}
            className="inline-flex items-center text-sm rounded-md px-3 py-2 border border-slate-700 text-slate-200 hover:border-blue-500/50"
            data-testid="equipment-report-pdf"
          >
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </button>
          <button
            type="button"
            onClick={() => openDownload("/api/equipment/report.csv", "equipment_report_csv", "all")}
            className="inline-flex items-center text-sm rounded-md px-3 py-2 border border-slate-700 text-slate-200 hover:border-blue-500/50"
            data-testid="equipment-report-csv"
          >
            <FileDown className="w-4 h-4 mr-2" /> CSV
          </button>
          <Button data-testid="equipment-new-btn" onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-500">
            <Plus className="w-4 h-4 mr-2" /> Add Equipment
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Items" value={stats.total} icon={PackageSearch} color="blue" testId="stat-total" />
        <StatCard label="In Storage" value={stats.inStorage} icon={Warehouse} color="slate" testId="stat-storage" />
        <StatCard label="In Van" value={stats.inVan} icon={Truck} color="amber" testId="stat-van" />
        <StatCard label="With Technician" value={stats.withTech} icon={UserIcon} color="emerald" testId="stat-tech" />
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Search by part #, serial, name</Label>
          <div className="relative mt-1.5">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
            <Input
              data-testid="equipment-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="MX808TS / serial / Autel..."
              className="pl-8 bg-[#030712] border-slate-800"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Location</Label>
          <select
            data-testid="equipment-filter-location"
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="mt-1.5 h-10 px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
          >
            <option value="all">All</option>
            <option value="storage">Storage</option>
            <option value="van">Van</option>
            <option value="technician">With Technician</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Brand</Label>
          <select
            data-testid="equipment-filter-brand"
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="mt-1.5 h-10 px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
          >
            <option value="all">All</option>
            {BRAND_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-400 mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-12 text-center" data-testid="equipment-empty">
          <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">No equipment yet</p>
          <p className="text-slate-500 text-sm mt-1">Add your first Autel scanner / target / calibration rig to start tracking.</p>
          <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-blue-600 hover:bg-blue-500" data-testid="empty-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Equipment
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <EquipmentCard key={it.id} item={it} />
          ))}
        </div>
      )}

      <CreateEquipmentDialog
        key={`create-${createOpen ? "o" : "c"}`}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(it) => { setCreateOpen(false); load(); navigate(`/equipment/${it.id}`); }}
      />
      <LabelSheetDialog
        key={`labels-${labelOpen ? "o" : "c"}`}
        open={labelOpen}
        items={items}
        onClose={() => setLabelOpen(false)}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  const colorMap = {
    blue: "border-blue-500/30 text-blue-300",
    slate: "border-slate-700 text-slate-300",
    amber: "border-amber-500/30 text-amber-300",
    emerald: "border-emerald-500/30 text-emerald-300",
    cyan: "border-cyan-500/30 text-cyan-300",
  };
  return (
    <div data-testid={testId} className={`rounded-xl border ${colorMap[color]} bg-[#0B1120] p-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function EquipmentCard({ item }) {
  const loc = item.location || "storage";
  const locConfig = {
    storage: { color: "bg-slate-800 border-slate-700 text-slate-300", icon: Warehouse, label: "Storage" },
    van: { color: "bg-amber-500/10 border-amber-500/30 text-amber-300", icon: Truck, label: "Van" },
    technician: { color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", icon: UserIcon, label: item.technician_name ? `Tech: ${item.technician_name}` : "Technician" },
  }[loc] || { color: "", icon: PackageSearch, label: loc };

  const LocIcon = locConfig.icon;

  return (
    <Link
      to={`/equipment/${item.id}`}
      data-testid={`equipment-card-${item.part_number}`}
      className="rounded-xl border border-slate-800 bg-[#0B1120] p-4 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all block"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{item.brand || "—"} · {item.model || "—"}</div>
          <div className="text-base font-semibold text-white mt-0.5 truncate">{item.name}</div>
          <div className="text-xs text-slate-400 mt-1 font-mono">{item.part_number}{item.serial_number ? ` · SN ${item.serial_number}` : ""}</div>
        </div>
        <Badge className={`${locConfig.color} flex items-center gap-1 shrink-0`}>
          <LocIcon className="w-3 h-3" /> {locConfig.label}
        </Badge>
      </div>
      {item.notes ? (
        <div className="mt-3 text-xs text-slate-500 line-clamp-2">{item.notes}</div>
      ) : null}
    </Link>
  );
}

function CreateEquipmentDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    part_number: "", name: "", brand: "Autel", model: "", serial_number: "",
    category: "scanner", location: "storage", technician_name: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/master/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
    }
  }, [open]);

  const save = async () => {
    if (!form.part_number.trim() || !form.name.trim()) {
      toast.error("Part Number and Name are required");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (body.location !== "technician") {
        body.technician_id = null;
        body.technician_name = null;
      } else {
        const t = technicians.find((x) => x.name === form.technician_name);
        if (t) body.technician_id = t.id;
      }
      const r = await api.post("/equipment", body);
      toast.success(`${r.data.name} added`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add equipment");
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B1120] border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="equipment-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4 text-blue-400" /> Add Equipment</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Register a new item to your inventory. A unique QR code will be auto-generated.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Part Number *" testId="eq-field-part-number">
            <Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })}
              className="bg-[#030712] border-slate-800 font-mono" placeholder="MX808TS-2024"
              data-testid="eq-input-part-number" />
          </Field>
          <Field label="Equipment Name *" testId="eq-field-name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#030712] border-slate-800" placeholder="MaxiSys Ultra ADAS Scanner"
              data-testid="eq-input-name" />
          </Field>
          <Field label="Brand" testId="eq-field-brand">
            <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
              className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
              data-testid="eq-input-brand">
              {BRAND_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Model" testId="eq-field-model">
            <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="bg-[#030712] border-slate-800" placeholder="MS909CV"
              data-testid="eq-input-model" />
          </Field>
          <Field label="Serial Number" testId="eq-field-serial">
            <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
              className="bg-[#030712] border-slate-800 font-mono"
              data-testid="eq-input-serial" />
          </Field>
          <Field label="Category" testId="eq-field-category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
              data-testid="eq-input-category">
              {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Initial Location *" testId="eq-field-location">
            <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
              data-testid="eq-input-location">
              <option value="storage">Storage</option>
              <option value="van">Van</option>
              <option value="technician">With Technician</option>
            </select>
          </Field>
          {form.location === "technician" && (
            <Field label="Technician" testId="eq-field-tech" span2>
              <select value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })}
                className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
                data-testid="eq-input-technician">
                <option value="">— Select technician —</option>
                {technicians.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </Field>
          )}
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1.5 bg-[#030712] border-slate-800 text-sm min-h-[60px]"
              placeholder="Calibration kit ID, accessories included, condition notes..."
              data-testid="eq-input-notes" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="eq-create-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Create + Generate QR
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId, span2 }) {
  return (
    <div className={span2 ? "col-span-2" : ""} data-testid={testId}>
      <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Print Labels Dialog — select items + grid → opens PDF in new tab
// ──────────────────────────────────────────────────────────────
function LabelSheetDialog({ open, items, onClose }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set(items.map((i) => i.id)));
  const [perPage, setPerPage] = useState(12);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedIds(new Set(items.map((i) => i.id)));
      setPerPage(12);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const selectAll = () => setSelectedIds(new Set(items.map((i) => i.id)));
  const selectNone = () => setSelectedIds(new Set());
  const selectByLocation = (loc) => setSelectedIds(new Set(items.filter((i) => i.location === loc).map((i) => i.id)));

  const generate = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one item");
      return;
    }
    setBusy(true);
    try {
      // POST returns a binary PDF — fetch as blob and open in new tab to bypass auth-header issue
      const resp = await api.post("/equipment/labels.pdf", {
        equipment_ids: Array.from(selectedIds),
        per_page: perPage,
      }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success(`Generated ${selectedIds.size} labels — ${perPage} per page`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate labels");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const pageCount = Math.ceil(selectedIds.size / perPage) || 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B1120] border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="labels-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="w-4 h-4 text-emerald-400" /> Print QR Labels</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Generate a PDF sheet of QR stickers. Print on adhesive sticker paper (LETTER size 8.5&quot;×11&quot;) and stick on each tool.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Grid selector */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Labels per Page</Label>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {[6, 12, 24, 30].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPerPage(n)}
                  className={`rounded-md border px-3 py-2 text-sm transition-all ${perPage === n ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}
                  data-testid={`labels-perpage-${n}`}
                >
                  <div className="font-bold">{n}</div>
                  <div className="text-[9px] uppercase tracking-wider mt-0.5 opacity-70">
                    {n === 6 && "Large"}
                    {n === 12 && "Medium"}
                    {n === 24 && "Small"}
                    {n === 30 && "Avery 5160"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quick select */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Select Items ({selectedIds.size}/{items.length})</Label>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
                <button onClick={selectAll} className="text-blue-300 hover:text-blue-200" data-testid="labels-select-all">All</button>
                <span className="text-slate-700">·</span>
                <button onClick={selectNone} className="text-slate-400 hover:text-slate-200" data-testid="labels-select-none">None</button>
                <span className="text-slate-700">·</span>
                <button onClick={() => selectByLocation("storage")} className="text-slate-400 hover:text-slate-200" data-testid="labels-select-storage">Storage</button>
                <span className="text-slate-700">·</span>
                <button onClick={() => selectByLocation("van")} className="text-amber-400 hover:text-amber-200" data-testid="labels-select-van">Van</button>
              </div>
            </div>
            <div className="rounded-md border border-slate-800 bg-slate-900/40 max-h-64 overflow-y-auto">
              {items.map((it) => {
                const checked = selectedIds.has(it.id);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => toggle(it.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 border-b border-slate-800/60 last:border-0 hover:bg-blue-500/5 text-left"
                    data-testid={`labels-item-${it.part_number}`}
                  >
                    {checked
                      ? <CheckSquare className="w-4 h-4 text-emerald-400 shrink-0" />
                      : <Square className="w-4 h-4 text-slate-600 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-200 truncate">{it.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{it.part_number} · {it.location}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview info */}
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-slate-300">
            <span className="font-semibold text-blue-300">{selectedIds.size}</span> labels × <span className="font-semibold text-blue-300">{perPage}</span> per page = <span className="font-semibold text-blue-300">{pageCount}</span> page(s).
            Each label shows QR + equipment name + part number.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={generate} disabled={busy || selectedIds.size === 0} className="bg-emerald-600 hover:bg-emerald-500" data-testid="labels-generate-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />}
            Generate PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
